import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { sortByEndDateDesc } from "../../../utils/sortByDate";
import { HiLink, HiBadgeCheck } from "react-icons/hi";
import Tooltip from '../../shared/Tooltip';
import { RichTextContent } from '../../../utils/richTextProcessor';
import { createProfileViewRequestAction } from '../../../redux/actions/userActions';
import { getSentProfileViewRequestsAction } from '../../../redux/actions/profileViewRequestActions';
import { useToast } from '../../../contexts/ToastContext';

const OthersTab = ({
  isAwardsExpanded,
  setIsAwardsExpanded,
  isLicensesExpanded,
  setIsLicensesExpanded,
  isAchievementsExpanded,
  setIsAchievementsExpanded,
  isMembershipsExpanded,
  setIsMembershipsExpanded,
  isLanguagesExpanded,
  setIsLanguagesExpanded,
  isOtherExpanded,
  setIsOtherExpanded,
  selectedOther,
  setSelectedOther,
  isOtherCardExpanded,
  setIsOtherCardExpanded,
  isDescriptionVisible,
  setIsDescriptionVisible,
  setVerificationOverlay,
  othersData, // New prop for transformed data
  isPrivateProfile = false,
  profileUserId = null,
  isAuthenticated = true,
  onRequireAuth = null,
  isViewingOwnProfile = false,
}) => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [requestLoading, setRequestLoading] = useState(false);
  
  // Get sent profile view requests from Redux
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);

  // Fetch sent requests on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && profileUserId && !isViewingOwnProfile) {
      dispatch(getSentProfileViewRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, profileUserId, isViewingOwnProfile]);

  // Find existing profile view request for this profile owner
  const existingProfileViewRequest = useMemo(() => {
    if (!profileUserId || !sentProfileViewRequests || sentProfileViewRequests.length === 0 || isViewingOwnProfile) {
      return null;
    }
    
    const found = sentProfileViewRequests.find((request) => {
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      return profileOwnerId && String(profileOwnerId) === String(profileUserId) && request.status === "pending";
    });
    
    return found;
  }, [sentProfileViewRequests, profileUserId, isViewingOwnProfile]);

  const handleRequestAccess = async () => {
    if (!isAuthenticated) {
      if (onRequireAuth) {
        onRequireAuth("auth_required");
      }
      return;
    }

    if (!profileUserId || isViewingOwnProfile) return;

    setRequestLoading(true);
    try {
      const result = await dispatch(createProfileViewRequestAction(profileUserId));
      if (result?.success) {
        showSuccess("Profile view request sent successfully");
        // Refresh sent requests to update button state
        await dispatch(getSentProfileViewRequestsAction(null, 25, 0));
      } else {
        showError(result?.error || "Failed to send request");
      }
    } catch (error) {
      showError("Error sending request");
    } finally {
      setRequestLoading(false);
    }
  };
  // Create a dynamic accordion state management
  const [accordionStates, setAccordionStates] = React.useState({
    Awards: isAwardsExpanded,
    Licenses: isLicensesExpanded,
    Achievements: isAchievementsExpanded,
    Memberships: isMembershipsExpanded,
    Languages: isLanguagesExpanded,
    Other: isOtherExpanded,
  });

  // State for expandable cards
  const [expandedCards, setExpandedCards] = React.useState({});

  // Function to toggle accordion state - only one can be open at a time
  const toggleAccordion = (category) => {
    setAccordionStates(prev => {
      const newState = {};
      // Close all accordions first
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      // Open only the clicked one if it wasn't already open
      const isOpening = !prev[category];
      newState[category] = isOpening;
      
      // If opening a section, select the first card but don't expand it
      if (isOpening) {
        let firstCardId = null;
        if (category === 'Awards' && awardsList.length > 0) {
          firstCardId = awardsList[0].id;
        } else if (category === 'Licenses' && licensesList.length > 0) {
          firstCardId = licensesList[0].id;
        } else if (category === 'Achievements' && achievementsList.length > 0) {
          firstCardId = achievementsList[0].id;
        } else if (category === 'Memberships' && membershipsList.length > 0) {
          firstCardId = membershipsList[0].id;
        } else if (category === 'Languages' && languagesList.length > 0) {
          firstCardId = languagesList[0].id;
        } else if (category === 'Other' && otherList.length > 0) {
          firstCardId = otherList[0].id;
        }
        
        if (firstCardId) {
          setSelectedOther(firstCardId);
          // Start with all cards collapsed
          setExpandedCards({});
        }
      }
      
      return newState;
    });
  };

  // Function to get accordion state
  const getAccordionState = (category) => {
    return accordionStates[category] || false;
  };

  // Function to toggle card expansion - only one card can be open at a time within the same section
  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => {
      const newState = { ...prev };
      
      // Find which section this card belongs to
      let currentSection = null;
      if (awardsList.some(award => award.id === cardId)) {
        currentSection = 'Awards';
      } else if (licensesList.some(license => license.id === cardId)) {
        currentSection = 'Licenses';
      } else if (achievementsList.some(achievement => achievement.id === cardId)) {
        currentSection = 'Achievements';
      } else if (membershipsList.some(membership => membership.id === cardId)) {
        currentSection = 'Memberships';
      } else if (languagesList.some(language => language.id === cardId)) {
        currentSection = 'Languages';
      } else if (otherList.some(otherItem => otherItem.id === cardId)) {
        currentSection = 'Other';
      }
      
      // Close all cards in the current section first
      if (currentSection === 'Awards') {
        awardsList.forEach(award => {
          newState[award.id] = false;
        });
      } else if (currentSection === 'Licenses') {
        licensesList.forEach(license => {
          newState[license.id] = false;
        });
      } else if (currentSection === 'Achievements') {
        achievementsList.forEach(achievement => {
          newState[achievement.id] = false;
        });
      } else if (currentSection === 'Memberships') {
        membershipsList.forEach(membership => {
          newState[membership.id] = false;
        });
      } else if (currentSection === 'Languages') {
        languagesList.forEach(language => {
          newState[language.id] = false;
        });
      } else if (currentSection === 'Other') {
        otherList.forEach(otherItem => {
          newState[otherItem.id] = false;
        });
      }
      
      // Open only the clicked card if it wasn't already open
      const isOpening = !prev[cardId];
      newState[cardId] = isOpening;
      return newState;
    });
  };

  // Function to get card expansion state
  const isCardExpanded = (cardId) => {
    return expandedCards[cardId] || false;
  };

  // Get others data from API only - no hardcoded fallback
  const awardsList = React.useMemo(() => sortByEndDateDesc(othersData?.awards || []), [othersData?.awards]);
  const licensesList = React.useMemo(() => sortByEndDateDesc(othersData?.licenses || []), [othersData?.licenses]);
  const achievementsList = React.useMemo(() => sortByEndDateDesc(othersData?.achievements || []), [othersData?.achievements]);
  const membershipsList = React.useMemo(() => sortByEndDateDesc(othersData?.memberships || []), [othersData?.memberships]);
  const languagesList = React.useMemo(() => sortByEndDateDesc(othersData?.languages || []), [othersData?.languages]);
  const otherList = React.useMemo(() => sortByEndDateDesc(othersData?.other || []), [othersData?.other]);

  // Initialize accordion states when others data changes
  React.useEffect(() => {
    const initialStates = {};
    if (awardsList.length > 0) {
      initialStates['Awards'] = true; // Open first section by default
    }
    if (licensesList.length > 0) {
      initialStates['Licenses'] = false;
    }
    if (achievementsList.length > 0) {
      initialStates['Achievements'] = false;
    }
    if (membershipsList.length > 0) {
      initialStates['Memberships'] = false;
    }
    if (languagesList.length > 0) {
      initialStates['Languages'] = false;
    }
    if (otherList.length > 0) {
      initialStates['Other'] = false;
    }
    if (Object.keys(initialStates).length > 0) {
      setAccordionStates(initialStates);
    }

    // Select first card but don't expand it by default
    if (awardsList.length > 0) {
      const firstAwardId = awardsList[0].id;
      setSelectedOther(firstAwardId);
      setExpandedCards({});
    }
  }, [awardsList, licensesList, achievementsList, membershipsList, languagesList, otherList, setSelectedOther]);

  if (isPrivateProfile) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        {/* Left - Others list area */}
        <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center">
          <svg
            className="w-8 h-8 text-amber-500 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading mb-1">
            Private account
          </h3>
          <p className="text-xs md:text-sm text-gray-600 font-roboto-flex mb-3">
            This is a private account.
          </p>
          {!isViewingOwnProfile && (
            <button
              onClick={handleRequestAccess}
              disabled={requestLoading || existingProfileViewRequest}
              className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                existingProfileViewRequest
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {requestLoading ? (
                "Sending..."
              ) : existingProfileViewRequest ? (
                "Request Pending"
              ) : (
                "Request MYST Access"
              )}
            </button>
          )}
        </div>

        {/* Right - Others details */}
        <div className="col-span-1 md:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm p-8 flex flex-col items-center justify-center text-center">
          <svg
            className="w-10 h-10 text-amber-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading mb-2">
            Private account
          </h2>
          <p className="text-sm text-gray-600 font-roboto-flex max-w-md">
            This is a private account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
      {/* Left Card - Others List */}
      <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-3 accordion-section">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading">
            Others
          </h3>
          {isPrivateProfile && (
            <svg
              className="w-4 h-4 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
              />
            </svg>
          )}
        </div>
        
        {/* Awards Accordion - Only show if there's data */}
        {awardsList.length > 0 && (
          <div className="mb-2 accordion-list">
            <button
              onClick={() => toggleAccordion('Awards')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Awards') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Awards')}
              data-tooltip="Awards"
              title="Awards"
            >
              <span className="accordion-header">Awards</span>
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {getAccordionState('Awards') && (
              <div className="space-y-2 mt-2">
                {awardsList.map((award) => (
                  <button
                    key={award.id}
                    onClick={() => {
                      setSelectedOther(award.id);
                      toggleCardExpansion(award.id);
                    }}
                    className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedOther === award.id
                        ? "text-primary font-bold"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    data-tooltip={award.title}
                    title={award.title}
                  >
                    {award.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Licenses Accordion - Only show if there's data */}
        {licensesList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Licenses')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Licenses') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Licenses')}
            data-tooltip="Licenses"
            title="Licenses"
          >
            <span className="accordion-header">Licenses</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Licenses') && (
            <div className="space-y-2 mt-2">
                {licensesList.map((license) => (
                <button
                    key={license.id}
                    onClick={() => {
                      setSelectedOther(license.id);
                      toggleCardExpansion(license.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedOther === license.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={license.title}
                    title={license.title}
                >
                    {license.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Achievements Accordion - Only show if there's data */}
        {achievementsList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Achievements')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Achievements') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Achievements')}
            data-tooltip="Achievements"
            title="Achievements"
          >
            <span className="accordion-header">Achievements</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Achievements') && (
            <div className="space-y-2 mt-2">
                {achievementsList.map((achievement) => (
                <button
                    key={achievement.id}
                    onClick={() => {
                      setSelectedOther(achievement.id);
                      toggleCardExpansion(achievement.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedOther === achievement.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={achievement.title}
                    title={achievement.title}
                >
                    {achievement.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Memberships Accordion - Only show if there's data */}
        {membershipsList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Memberships')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Memberships') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Memberships')}
            data-tooltip="Memberships"
            title="Memberships"
          >
            <span className="accordion-header">Memberships</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Memberships') && (
            <div className="space-y-2 mt-2">
                {membershipsList.map((membership) => (
                <button
                    key={membership.id}
                    onClick={() => {
                      setSelectedOther(membership.id);
                      toggleCardExpansion(membership.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedOther === membership.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={membership.title}
                    title={membership.title}
                >
                    {membership.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Languages Accordion - Only show if there's data */}
        {languagesList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Languages')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Languages') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Languages')}
            data-tooltip="Languages"
            title="Languages"
          >
            <span className="accordion-header">Languages</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Languages') && (
            <div className="space-y-2 mt-2">
                {languagesList.map((language) => (
                <button
                    key={language.id}
                    onClick={() => {
                      setSelectedOther(language.id);
                      toggleCardExpansion(language.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedOther === language.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={language.title}
                    title={language.title}
                >
                    {language.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Other Accordion - Only show if there's data */}
        {otherList.length > 0 && (
        <div className="mb-2 accordion-list">
          <button
              onClick={() => toggleAccordion('Other')}
              className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
                getAccordionState('Other') ? 'expanded' : ''
              }`}
              aria-expanded={getAccordionState('Other')}
            data-tooltip="Other"
            title="Other"
          >
            <span className="accordion-header">Other</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
            {getAccordionState('Other') && (
            <div className="space-y-2 mt-2">
                {otherList.map((otherItem) => (
                <button
                    key={otherItem.id}
                    onClick={() => {
                      setSelectedOther(otherItem.id);
                      toggleCardExpansion(otherItem.id);
                    }}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item truncate-text ${
                      selectedOther === otherItem.id
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                    data-tooltip={otherItem.title}
                    title={otherItem.title}
                >
                    {otherItem.title}
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
      
      {/* Right Card - Others Details */}
      <div className="col-span-1 md:col-span-3 space-y-3">
        {/* Awards Cards - Only show when Awards accordion is expanded and has data */}
        {getAccordionState('Awards') && awardsList.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Awards</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {awardsList.map((award, index) => {
                const isExpanded = isCardExpanded(award.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={award.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedOther(award.id);
                      toggleCardExpansion(award.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {award.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(award.validationData) && award.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: award.title,
                                  validationData: award.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && award.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{award.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">
                        {award.date ? new Date(parseInt(award.date)).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Date not specified'}
                      </p>

                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {award.description && (
                  <RichTextContent 
                    content={award.description}
                    className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                  />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Licenses Cards - Only show when Licenses accordion is expanded and has data */}
        {getAccordionState('Licenses') && licensesList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Licenses</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {licensesList.map((license, index) => {
                const isExpanded = isCardExpanded(license.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={license.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedOther(license.id);
                      toggleCardExpansion(license.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {license.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(license.validationData) && license.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: license.title,
                                  validationData: license.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && license.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{license.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{license.duration}</p>

                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {license.description && (
                  <RichTextContent 
                    content={license.description}
                    className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                  />
                          )}
                 </div>
               )}
             </div>
           </div>
                );
              })}
              </div>
          </div>
        )}

        {/* Memberships Cards - Only show when Memberships accordion is expanded and has data */}
        {getAccordionState('Memberships') && membershipsList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Memberships</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {membershipsList.map((membership, index) => {
                const isExpanded = isCardExpanded(membership.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={membership.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedOther(membership.id);
                      toggleCardExpansion(membership.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {membership.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(membership.validationData) && membership.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: membership.title,
                                  validationData: membership.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {membership.attachmentExists && (
                              <Tooltip text="Attachment available on MYST">
                                <HiLink className="w-4 h-4 text-gray-600" />
                              </Tooltip>
                            )}
                        </div>
                    </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{membership.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{membership.duration}</p>
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {membership.description && (
                            <RichTextContent
                              content={membership.description}
                              className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Languages Cards - Only show when Languages accordion is expanded and has data */}
        {getAccordionState('Languages') && languagesList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Languages</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {languagesList.map((language, index) => {
                const isExpanded = isCardExpanded(language.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={language.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${isExpanded ? 'col-span-2' : ''}`}
                    onClick={() => {
                      setSelectedOther(language.id);
                      toggleCardExpansion(language.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>{language.title}</h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(language.validationData) && language.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: language.title,
                                  validationData: language.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && language.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {language.skillsText && (
                        <p className="font-flex-regular text-gray-500 text-xs mb-2">{language.skillsText}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Achievements Cards - Only show when Achievements accordion is expanded and has data */}
        {getAccordionState('Achievements') && achievementsList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Achievements</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {achievementsList.map((achievement, index) => {
                const isExpanded = isCardExpanded(achievement.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={achievement.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedOther(achievement.id);
                      toggleCardExpansion(achievement.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {achievement.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(achievement.validationData) && achievement.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: achievement.title,
                                  validationData: achievement.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {isExpanded && achievement.attachmentExists && (
                            <Tooltip text="Attachment available on MYST">
                              <HiLink className="w-4 h-4 text-gray-600" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{achievement.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{achievement.duration}</p>
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {achievement.description && (
                            <RichTextContent
                              content={achievement.description}
                              className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Other Cards - Only show when Other accordion is expanded and has data */}
        {getAccordionState('Other') && otherList.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <h4 className="font-semibold text-gray-800 mb-3 font-heading text-base">Other</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {otherList.map((other, index) => {
                const isExpanded = isCardExpanded(other.id);
                const hasAnyExpanded = Object.values(expandedCards).some(expanded => expanded);
                const shouldShow = !hasAnyExpanded || isExpanded;
                
                if (!shouldShow) return null;
                
                return (
                  <div
                    key={other.id}
                    className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                      isExpanded ? 'col-span-2' : ''
                    }`}
                    onClick={() => {
                      setSelectedOther(other.id);
                      toggleCardExpansion(other.id);
                    }}
                  >
                    <div className={`${isExpanded ? 'p-4' : 'p-3'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h5 className={`font-semibold text-gray-900 font-heading leading-tight ${isExpanded ? 'text-base' : 'text-sm'}`}>
                          {other.title}
                        </h5>
                        <div className="flex items-center gap-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCards({});
                                setVerificationOverlay({ show: false });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          {isExpanded && Array.isArray(other.validationData) && other.validationData.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setVerificationOverlay({
                                  show: true,
                                  title: other.title,
                                  validationData: other.validationData || []
                                });
                              }}
                              className="cursor-pointer hover:scale-110 transition-transform duration-200"
                            >
                              <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            </button>
                          )}
                          {other.attachmentExists && (
                              <Tooltip text="Attachment available on MYST">
                                <HiLink className="w-4 h-4 text-gray-600" />
                              </Tooltip>
                            )}
                        </div>
                    </div>
                      <p className="font-flex-regular text-gray-600 text-xs mb-1">{other.organization}</p>
                      <p className="font-flex-regular text-gray-500 text-xs mb-2">{other.duration}</p>
                      {isExpanded && (
                        <div className="mt-3 space-y-2">
                          {other.description && (
                            <RichTextContent
                              content={other.description}
                              className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Show message if no accordion is expanded or no others data */}
        {!getAccordionState('Awards') && !getAccordionState('Licenses') && !getAccordionState('Achievements') && 
         !getAccordionState('Memberships') && !getAccordionState('Languages') && !getAccordionState('Other') && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm font-medium">Select a category from the left to view details</p>
            </div>
          </div>
              )}

        {/* Show message if accordion is expanded but no data */}
        {(getAccordionState('Awards') && awardsList.length === 0) ||
         (getAccordionState('Licenses') && licensesList.length === 0) ||
         (getAccordionState('Achievements') && achievementsList.length === 0) ||
         (getAccordionState('Memberships') && membershipsList.length === 0) ||
         (getAccordionState('Languages') && languagesList.length === 0) ||
         (getAccordionState('Other') && otherList.length === 0) ? (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm font-medium">No data available for this category</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OthersTab;
